import { InboxIcon, NoSymbolIcon } from '@heroicons/react/24/outline';
import { CheckedState } from '@radix-ui/react-checkbox';
import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { Row, SortingState } from '@tanstack/react-table';
import { isEmpty } from 'radash';
import { memo, PropsWithChildren, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Grid } from '@/components/core/Grid';
import { useAttributesStateStore } from '@/components/datamodel/hooks/useAttributesStore';
import { useValidationsStore } from '@/components/datamodel/hooks/useValidationsStore';
import { BaseValidation, Validation } from '@/components/datamodel/types';
import {
    DataModelDependentPage,
    DeploymentInProgress,
    NoDataModelEmptyState,
} from '@/components/shared';
import { useItemValidationUpdater } from '@/dialogs/item-editor/hooks/useItemValidationUpdater';
import { AUTH_OBJECT_TYPES, useAuth } from '@/hooks/useAuth';
import useAutoFetchIfSpace from '@/hooks/useAutoFetchIfSpace';
import { useDataModel } from '@/hooks/useDataModel';
import { useDynamicPageSize } from '@/hooks/useDynamicPageSize';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { useOrganization } from '@/hooks/useOrganization';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';
import useWatchListService from '@/hooks/useWatchlistService';
import { getInjector, getRootScope } from '@/services/angularjs-bridge';
import { logger } from '@/services/logger';
import { PersistedItem } from '@/types/DataModel';
import { ItemsQuery } from '@/types/ItemsQuery';

import CatalogAttribute from '../catalog-attributes/CatalogAttribute';
import { Button } from '../core/Button';
import { Icon } from '../core/Icon';
import { Loader } from '../core/Loader';
import { setLastBrowseItemCount } from './browseItemCount';
import { EditableItemCell } from './EditableItemCell';
import { EditableRowFormProvider } from './EditableRowFormProvider';
import { EditingRowProvider, useEditingRow } from './hooks/useEditingRow';
import type {
    SaveSuccessArgs,
    ValidateSuccessArgs,
} from './hooks/useItemsListChangesQueues';
import { useItemsListRealtimeUpdates } from './hooks/useItemsListRealtimeUpdates';
import useItemsData from './itemsList.queries';
import type { ItemsListMeta } from './itemsList.types';
import { filterHitListSorting, isHitListColumnSortable } from './isHitListColumnSortable';
import { columns } from './itemsListColumn';
import { syncHitListCellTitle } from './syncHitListCellTitle';

// LAX-10042: pagination / sort keys that the AngularJS fetch-item-controller
// merges into the `NewFetchItems` payload on top of the actual restrictions
// (webapp/app/controllers/fetch-item-controller.js#doFetchItems lines
// 434-446). These are not user-driven filters and must not count towards
// `hasRestrictions()`.
const PAGINATION_QUERY_PARAM_KEYS: ReadonlySet<string> = new Set([
    'fields',
    'page',
    'count',
    'sort',
    'direction',
]);

// LAX-10043: parity with the legacy ErrorCode constant
// (webapp/app/lax.js#94-101). Only the codes we branch on in the React
// error handler are duplicated here; adding to this set means also
// mirroring it in the AngularJS constant.
const ERROR_CODE_SEARCH_QUERY_INVALID = 301;
const ERROR_CODE_SEARCH_QUERY_TOO_LONG = 302;

const ItemsListCatalogCell = memo(
    function ItemsListCatalogCell({
        attribute,
        item,
    }: {
        attribute: any;
        item: any;
    }) {
        const value = item[attribute.name];
        const isIconCell =
            attribute.typeName === 'Image' ||
            attribute.typeName === 'Document' ||
            attribute.baseClass === 'Image' ||
            attribute.baseClass === 'Document';
        // LAX-10037 / LAX-10014: always render the wrapper (even when
        // the value is empty) so catalog rows stay a uniform tw-h-10.
        // Hover title is the visible cell text, same as the legacy
        // `ui-grid-browser-tooltips` directive. `singleLine` matches
        // the browse cell: one line + ellipsis.
        return (
            <div
                data-hit-list-icon-cell={isIconCell ? 'true' : undefined}
                onMouseEnter={syncHitListCellTitle}
                className={isIconCell
                    ? 'tw-h-full tw-w-full'
                    : 'tw-flex tw-h-full tw-w-full tw-items-center tw-justify-start tw-min-w-0 tw-truncate'}
            >
                {value ? (
                    <CatalogAttribute
                        attribute={attribute}
                        item={item}
                        withoutLabel={true}
                        gridMode={true}
                        singleLine={true}
                    />
                ) : null}
            </div>
        );
    },
    (prev, next) =>
        prev.item === next.item && prev.attribute.name === next.attribute.name,
);

/**
 * Row wrapper used by the browse grid: mounts `EditableRowFormProvider`
 * only for the row that is currently in edit mode. Other rows render with
 * no form context, so we do not pay RHF cost on 100+ read-only rows.
 */
const BrowseRowFormWrapper = ({
    item,
    children,
}: PropsWithChildren<{ item: PersistedItem }>) => {
    const editingRow = useEditingRow();
    if (!editingRow || editingRow.editingRowId !== item.primaryKey__) {
        return <>{children}</>;
    }
    return <EditableRowFormProvider row={item}>{children}</EditableRowFormProvider>;
};

interface ItemsListComponentProps {
    selectAllItems: () => void;
    clearSelectedItems: () => void;
    onItemRowClick?: (row: any) => void;
    handleSelectedItems: (rows: any []) => void;
    isCatalogTable: boolean;
    loadWatchlistItems: any;
    redirectToItemDetail?: (item:any) => void;
    handleFetchedItems?: (items: any[]) => void;
    showEditor?: (row: any, primaryKey__?:string, editorState?: { onShowItemDetails: true }, items?:any) => void;
    deleteItem?: (row: any) => void;
    showItemReview?: (item: any) => void;
    // LAX-10042: wired from items-controller.js (`$scope.createNewItem`) so
    // the React "no items yet" empty state can offer the same NEW_ITEM CTA
    // as the AngularJS `browse.tpl.html` empty state.
    createNewItem?: () => void;
    /** LAX-10133: React-owned browse query restrictions from {@link useBrowseQuery}. */
    query?: ItemsQuery;
    /** LAX-10133: suppress the in-grid label when {@link SearchFilterBar} renders it. */
    hideFoundItemsLabel?: boolean;
    /** LAX-10133: surfaces fetch counts to the browse search bar. */
    onItemsMetaChange?: (meta: ItemsListMeta) => void;
}

function ItemsListInner({
    selectAllItems,
    clearSelectedItems,
    onItemRowClick,
    handleSelectedItems,
    isCatalogTable = false,
    loadWatchlistItems,
    redirectToItemDetail,
    handleFetchedItems,
    showEditor,
    deleteItem,
    showItemReview,
    createNewItem,
    query,
    hideFoundItemsLabel = false,
    onItemsMetaChange,
}: ItemsListComponentProps) {
    const editingRow = useEditingRow();
    const $angularJSInjector = getInjector();
    const $rootScope = getRootScope($angularJSInjector);
    const { t } = useTranslation();
    const { auth } = useAuth();
    const {watchListService} = useWatchListService();
    const [itemWatchList, setItemWatchList] = useLocalStorage<any>(
        'itemWatchlist',
        {
            primaryKeys: []
        },
    );

    const [queryParams, setQueryParams] = useState<any>(undefined);
    // Sort/direction are kept in a dedicated state (not merged into
    // `queryParams`) so that Angular's `NewFetchItems` broadcast - which
    // replaces `queryParams` wholesale with its own {fields, count, page, ...}
    // payload - cannot wipe the React-owned sort selection. This is what makes
    // reloads keep the multi-column sort caret AND its wire params in sync
    // (LAX-10036).
    const [sortParams, setSortParams] = useState<{ sort?: string[]; direction?: string[] }>({});
    const [allItemsSelected, setAllItemsSelected] = useState<boolean>(false);
    const [selectedItems, setSelectedItems] = useState<any[]>([]);
    const pageSize = useDynamicPageSize();
    const containerRef = useRef<HTMLDivElement>(null);
    const dirtyItemIdsRef = useRef<string[]>([]);

    const {
        data,
        isLoading,
        isFetching,
        isFetchingNextPage,
        fetchNextPage,
        hasNextPage,
        refetch,
        // LAX-10043: expose fetch errors so we can mirror the legacy
        // `fetch-item-controller.js#errorFn` (lines 581-601) - open the
        // bulk-search modal on SEARCH_QUERY_TOO_LONG when BULK_SEARCH is
        // enabled, and toast ERROR_LOADING_ITEMS otherwise (except for
        // SEARCH_QUERY_INVALID, which is displayed inline by the search
        // bar itself).
        error,
        isError,
    } = useItemsData({
        variables: {
            limit: pageSize,
            ...queryParams,
            ...sortParams
        },
        select: (data) => {
            const itemsData = data.pages.flatMap((page) => page.data);
            const itemsFound = data.pages[0]?.itemsFound;
            const itemsMax = data.pages[0]?.itemsMax;
            return {
                itemsData,
                itemsFound,
                itemsMax
            };
        },
        enabled: !!queryParams,
        // Keep previously loaded rows visible during sort/filter refetch so the Grid body
        // doesn't collapse to a single full-width loader row, which would clamp horizontal
        // scrollLeft to 0 and lose the sorted column from view (LAX-9797).
        placeholderData: keepPreviousData,
    });
    const { itemsData, itemsFound, itemsMax } = data || {};
    const { toast } = useToast();

    useEffect(() => {
        if (itemsFound == null) {
            return;
        }

        setLastBrowseItemCount(
            Number.parseInt(itemsFound, 10) || 0,
            Number.parseInt(itemsMax ?? '0', 10) || 0,
        );
    }, [itemsFound, itemsMax]);

    useEffect(() => {
        if (!onItemsMetaChange || itemsData == null) {
            return;
        }

        onItemsMetaChange({
            visibleRowCount: itemsData.length,
            numberFound: Number.parseInt(itemsFound ?? '0', 10) || 0,
            maxNumberFound: Number.parseInt(itemsMax ?? '0', 10) || 0,
            hasCursor: !!hasNextPage,
            isFetching: isFetching && !isFetchingNextPage,
        });
    }, [
        onItemsMetaChange,
        itemsData,
        itemsFound,
        itemsMax,
        hasNextPage,
        isFetching,
        isFetchingNextPage,
    ]);

    // Item Library uses browse; Online Catalog uses catalog. Hardcoding catalog
    // hid browse-only columns such as Basic UDI-DI / UDI-DIs Link (LAX-10017).
    const currentLayout = isCatalogTable ? 'catalog' : 'browse';
    const { dataModel } = useDataModel();

    // LAX-10039: mirror the legacy `items-controller.js#getGridStateKey()`
    // (lines 636-650) so the browse grid persists column order / widths /
    // sort per category rather than under a single shared key. Selection
    // views and NO_CATEGORY are opted out of persistence (return `undefined`
    // to skip the sub-key) — same as legacy which returned `null`. Catalog
    // keeps a single default state (catalog-controller.js hard-codes
    // `DEFAULT_GRID_STATE_KEY` regardless of category).
    const storedStateSubKey = useMemo<string | undefined>(() => {
        if (isCatalogTable) return undefined;
        const routeParams = $angularJSInjector.get('$routeParams');
        if (routeParams?.selectionId != null || routeParams?.category === 'NO_CATEGORY') {
            return undefined;
        }
        if (routeParams?.category) {
            return `${routeParams.category}-state`;
        }
        return 'default-state';
        // queryParams is the trigger: NewFetchItems fires on every category /
        // selectionId change and re-reads $routeParams here, matching how
        // currentLayoutAttributes below tracks the same route state.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCatalogTable, queryParams]);

    // LAX-10038: mirror the legacy items-controller.js#createItemTableColumnDefinition
    // (lines 446-472) - browse columns must be the intersection of the layout
    // attributes and the current category's attributes (or the union of watchlist
    // category attributes when a selectionId is active). Fall back to the full
    // layout when the intersection is empty, so a category with no overlap does
    // not render an empty grid. Catalog stays unfiltered (catalog-controller.js
    // has no equivalent intersection).
    const currentLayoutAttributes = useMemo(() => {
        const layoutAttrs = dataModel?.filteredLayoutAttributes(currentLayout) ?? [];
        if (isCatalogTable) return layoutAttrs;

        const routeParams = $angularJSInjector.get('$routeParams');
        const categories: string[] = [];
        if (routeParams?.category && routeParams.category !== 'NO_CATEGORY') {
            categories.push(routeParams.category);
        } else if (routeParams?.selectionId != null) {
            const watchlistCategories =
                watchListService?.getWatchlistCategories?.() ?? [];
            categories.push(...watchlistCategories);
        }

        if (categories.length === 0) return layoutAttrs;

        const categoryAttrNames = new Set<string>();
        categories.forEach((category) => {
            (dataModel?.categoryAttributes(category) ?? []).forEach((attr) =>
                categoryAttrNames.add(attr.name),
            );
        });
        const intersected = layoutAttrs.filter((attr) =>
            categoryAttrNames.has(attr.name),
        );
        return intersected.length > 0 ? intersected : layoutAttrs;
        // queryParams is the trigger: NewFetchItems fires on every category /
        // selectionId change (fetch-item-controller.js#doFetchItems), which
        // updates queryParams and forces this memo to re-read $routeParams.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataModel, currentLayout, isCatalogTable, queryParams, watchListService]);

    const reactQueryRef = useRef(query);
    reactQueryRef.current = query;

    // LAX-10133: when the browse page passes a React-owned query, bootstrap
    // (and refresh) the fetch payload without waiting for Angular's
    // `NewFetchItems` broadcast.
    useEffect(() => {
        if (!query || !dataModel) {
            return;
        }

        const fields = currentLayoutAttributes.map((attribute) => attribute.name);
        if (fields.length === 0) {
            return;
        }

        const restrictions = Object.fromEntries(
            Object.entries(query).filter(([, value]) => value != null && value !== ''),
        );

        setQueryParams({
            ...restrictions,
            fields,
            page: 1,
            count: pageSize,
        });
    }, [query, dataModel, currentLayoutAttributes, pageSize]);

    const layoutColumns = useMemo(() => {
        // Browse: cells double-click into edit mode via EditableItemCell.
        // Catalog stays read-only (matches legacy: catalog-controller.js has
        // no inline edit pipeline).
        const CellComponent = isCatalogTable ? ItemsListCatalogCell : EditableItemCell;
        return currentLayoutAttributes.map((attr) => ({
            accessorKey: attr.name,
            header: attr.label,
            size: 150,
            params: attr.params,
            enableResizing: true,
            enableSorting: isHitListColumnSortable(dataModel?.attribute?.(attr.name) ?? attr),
            cell: ({ row }: any) => (
                <CellComponent attribute={attr} item={row.original} />
            ),
        }));
    }, [currentLayoutAttributes, isCatalogTable, dataModel]);

    const handleSelectAll = (checked: CheckedState) => {
        // LAX-10040 / LAX-10056: defensive guard for parity with the
        // AngularJS rowSelectionChangedBatch handler (items-controller.js
        // lines 603-615).
        if (allItemsSelected) return;

        const visibleItems = itemsData ?? [];

        if (checked) {
            // LAX-10040: iterate row-by-row through addWatchlistItem instead
            // of overwriting the primary-key list via setWatchlistPrimaryKeys.
            // Only add/removeWatchlistItem maintain the watchlist's category
            // bookkeeping (primaryKeyCategory / categoryPrimaryKeys /
            // categories); setWatchlistPrimaryKeys only patches primaryKeys
            // and would leave those maps stale.
            visibleItems.forEach((item) =>
                watchListService?.addWatchlistItem?.(item),
            );
            setSelectedItems(visibleItems);
            // Sync React's local mirror from the service so items selected on
            // pages already loaded (but not currently visible after a filter)
            // are not dropped.
            const allPrimaryKeys =
                watchListService?.getWatchlistPrimaryKeys?.() ?? [];
            setItemWatchList({ primaryKeys: allPrimaryKeys });
            if ($rootScope) {
                $rootScope.selectedItems = visibleItems;
                if (!$rootScope.$$phase) {
                    $rootScope.$apply();
                }
            }
        } else {
            // LAX-10040: on header uncheck, drop only the visible rows —
            // selections on other pages must survive (matches rowSelection
            // ChangedBatch, which only receives rows whose selection state
            // actually changed).
            visibleItems.forEach((item) =>
                watchListService?.removeWatchlistItem?.(item),
            );
            const remainingPrimaryKeys =
                watchListService?.getWatchlistPrimaryKeys?.() ?? [];
            setItemWatchList({ primaryKeys: remainingPrimaryKeys });
            setSelectedItems([]);
            if ($rootScope) {
                $rootScope.selectedItems = [];
                if (!$rootScope.$$phase) {
                    $rootScope.$apply();
                }
            }
        }
    };

    const handleSelectRows = (primaryKey: string) => {
        const newSelected = { ...itemWatchList };
        const index = newSelected.primaryKeys.findIndex(
            (pK: string) => primaryKey === pK,
        );

        if (index !== -1) {
            newSelected.primaryKeys = newSelected.primaryKeys.filter((pK: string) => pK !== primaryKey);
        } else {
            newSelected.primaryKeys = [...newSelected.primaryKeys, primaryKey];
        }
        setItemWatchList(newSelected);
    };

    const _handleSelectedItems = (item: any) => {
        // LAX-10040 / LAX-10056: in "select all across pages" mode the row
        // checkboxes are visually disabled, but guard the handler defensively
        // so any non-UI trigger (keyboard, tests, custom service) cannot
        // silently corrupt the watchlist. Mirrors the AngularJS
        // `if ($scope.allItemsSelected) return;` short-circuit in
        // items-controller.js#rowSelectionChanged.
        if (allItemsSelected) return;

        const wasSelected = selectedItems.some(
            (i: any) => i.primaryKey__ === item.primaryKey__,
        );
        const newSelectedItems = wasSelected
            ? selectedItems.filter(
                (i: any) => i.primaryKey__ !== item.primaryKey__,
            )
            : [...selectedItems, item];

        // LAX-10040: call add/removeWatchlistItem symmetrically (mirrors
        // items-controller.js lines 592-602). Using only addWatchlistItem
        // or overwriting via setWatchlistPrimaryKeys would leave the
        // watchlist's category bookkeeping (primaryKeyCategory / categories /
        // categoryPrimaryKeys) out of sync with primaryKeys — stale
        // categories then pollute `getWatchlistCategories()` and the
        // "Show selected" view.
        if (wasSelected) {
            watchListService?.removeWatchlistItem?.(item);
        } else {
            watchListService?.addWatchlistItem?.(item);
        }

        setSelectedItems(newSelectedItems);
        handleSelectedItems(newSelectedItems);
        handleSelectRows(item.primaryKey__);
    };

    const handelClearAllSelection = () => {
        const _itemWatchList = { ...itemWatchList };
        _itemWatchList.primaryKeys = [];
        setItemWatchList(_itemWatchList);
        setSelectedItems([]);

        if ($rootScope) {
            $rootScope.selectedItems = [];
        }
        clearSelectedItems();
        setAllItemsSelected(false);
    };

    const handelSelectAll = () => {
        // LAX-10056: track select-all via the flag only; keep the watchlist empty so
        // a page reload does not leave the previously loaded rows selected.
        setItemWatchList({ primaryKeys: [] });
        watchListService?.setWatchlistPrimaryKeys?.([]);
        setSelectedItems([]);
        if ($rootScope) {
            $rootScope.selectedItems = [];
        }
        setAllItemsSelected(true);
        selectAllItems();
    };
    const handleSelectedRow = (row: any) => {
        if (onItemRowClick) {
            onItemRowClick(row);
        }
    };

    const handleInfiniteScroll = useCallback(() => {
        if (hasNextPage) {
            fetchNextPage();
        }
    }, [hasNextPage, fetchNextPage]);

    const handelLoadWatchListItems = () => {
        loadWatchlistItems();
    };
    const sortableColumnIds = useMemo(() => {
        const ids = new Set<string>();
        currentLayoutAttributes.forEach((attr) => {
            if (isHitListColumnSortable(dataModel?.attribute?.(attr.name) ?? attr)) {
                ids.add(attr.name);
            }
        });
        return ids;
    }, [currentLayoutAttributes, dataModel]);

    const handleSortingChange: (storedState: SortingState) => void = (sorting: SortingState) => {
        // LAX-10282: drop persisted / restored sort on columns the search
        // index cannot sort so we never send `sort=hierarchyLink`.
        const sortableSorting = filterHitListSorting(
            sorting,
            (columnId) => sortableColumnIds.has(columnId),
        );
        if (sortableSorting.length > 0) {
            // Mirror the legacy `items-controller.js` `sortChanged` contract
            // (LAX-10036): send ordered `sort[] / direction[]` arrays so
            // Shift+click multi-column sort is applied by the backend in
            // priority order. The endpoint binds these via
            // `Page.sort` / `Page.direction` (`List<String>` @QueryParams).
            setSortParams({
                sort: sortableSorting.map((s) => s.id),
                direction: sortableSorting.map((s) => (s.desc ? 'desc' : 'asc')),
            });
            return;
        }
        // No column is sorted anymore ("unsorted"): drop the sort params so a
        // fresh search is issued. Otherwise the query key stays unchanged and
        // paging keeps using the previous sorted cursor, which triggers an
        // internal search error (LAX-9997).
        setSortParams((prev) => (
            prev.sort === undefined && prev.direction === undefined ? prev : {}
        ));
    };
    // LAX-10056: the header "select all (visible)" checkbox must reflect whether
    // every currently loaded row is actually selected - not merely whether the
    // watchlist happens to have the same length. Otherwise switching lists (e.g.
    // browse -> catalog) can carry over a watchlist of equal size and wrongly
    // show the header as checked while loaded rows are unselected.
    const areAllRowsSelected = allItemsSelected || (
        !!itemsData && itemsData.length > 0 &&
        (itemWatchList.primaryKeys?.length ?? 0) > 0 &&
        itemsData.every((item: any) => itemWatchList.primaryKeys.includes(item.primaryKey__))
    );

    // Legacy items-controller.js scattered `stopItemChangesQueues()` +
    // `stopGridCellEditListeners()` around every modal open (lines
    // 923-1425). Mirror it here by flushing the in-flight edit before
    // firing a modal action so the store queue's pending save is not
    // dropped by the row unmount that follows.
    const showEditorWithPause = useMemo<typeof showEditor>(() => {
        if (!showEditor) return undefined;
        return (row, primaryKey__, editorState, items) => {
            editingRow?.pauseEditing();
            showEditor(row, primaryKey__, editorState, items);
        };
    }, [showEditor, editingRow]);

    const deleteItemWithPause = useMemo<typeof deleteItem>(() => {
        if (!deleteItem) return undefined;
        return (row) => {
            editingRow?.pauseEditing();
            deleteItem(row);
        };
    }, [deleteItem, editingRow]);

    const showItemReviewWithPause = useMemo<typeof showItemReview>(() => {
        if (!showItemReview) return undefined;
        return (item) => {
            editingRow?.pauseEditing();
            showItemReview(item);
        };
    }, [showItemReview, editingRow]);

    const ItemsGridColumns = columns(
        t,
        itemsData,
        layoutColumns,
        itemWatchList.primaryKeys,
        areAllRowsSelected,
        allItemsSelected,
        handleSelectAll,
        _handleSelectedItems,
        isCatalogTable,
        redirectToItemDetail,
        showEditorWithPause,
        deleteItemWithPause,
        showItemReviewWithPause
    );

    // Only wrap the currently edited row's cells in an RHF FormProvider;
    // read-only rows stay outside the form and avoid the RHF cost.
    const rowWrapper = useMemo(() => {
        if (isCatalogTable) return undefined;
        function renderRowWrapper(row: Row<PersistedItem>, children: ReactNode) {
            return (
                <BrowseRowFormWrapper item={row.original}>
                    {children}
                </BrowseRowFormWrapper>
            );
        }
        return renderRowWrapper;
    }, [isCatalogTable]);

    useEffect(() => {
        // LAX-10167: `undefined` means no fetch has resolved yet, whereas `[]` is a
        // real result. Skipping the empty case would leave the previous rows in
        // $rootScope.selectedItems, and fetch-item-controller.js#getSelectedFullItems
        // reads exactly that in NEXT_HIT_LIST mode - a search with no hits would keep
        // Copy Item enabled for a row that is no longer in the list.
        if (itemsData) {
            const selectedRows = itemsData.filter((item) =>
                itemWatchList.primaryKeys.includes(item.primaryKey__)
            );
            setSelectedItems(selectedRows);
            handleSelectedItems(selectedRows);
        }
        if(handleFetchedItems && itemsData) {
            handleFetchedItems(itemsData);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemsData]);

    useEffect(() => {
        const handleNewFetchItems = (_event: any, data: any) => {
            if (reactQueryRef.current) {
                return;
            }
            if (queryParams !== data) {
                setQueryParams(data);
            }
        };

        const fetchNewItems = $rootScope.$on('NewFetchItems', handleNewFetchItems);

        const offRefetchItems = $rootScope.$on('RefetchItems', function () {
            refetch();
        });

        // LAX-10056: mirror Angular clearing the selection into the React state.
        // Angular is the source of truth for leaving "select all" mode (e.g. on a
        // search/filter change it emits 'NONE'); without this the React banner and
        // disabled checkboxes stay stuck on "all selected" until the delayed
        // NewFetchItems broadcast arrives. Reset the state directly here instead of
        // calling clearSelectedItems() - that would re-emit 'NONE' and loop.
        const offSelectionUpdated = $rootScope.$on('selectedItemsUpdated', function (_event: any, items: any) {
            if (items === 'NONE') {
                setItemWatchList({ primaryKeys: [] });
                setSelectedItems([]);
                if ($rootScope) {
                    $rootScope.selectedItems = [];
                }
                setAllItemsSelected(false);
            }
        });

        // Replay the latest broadcast in case Angular fired NewFetchItems before
        // this listener was registered (LAX-9847).
        const stashed = $rootScope.lastNewFetchItems;
        if (stashed && stashed.queryParams) {
            setQueryParams(stashed.queryParams);
        }

        return () => {
            fetchNewItems();
            offRefetchItems();
            offSelectionUpdated();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        $rootScope.$broadcast('getItems', itemsData);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemsData, queryParams]);

    // LAX-10043: mirror `items-controller.js#rowsRendered` (line 627-629).
    // The legacy grid fires `reloadBrowseListFileTypes` on every render
    // cycle so the `filetype` directive (webapp/app/directives/
    // directives.js line 1014) can rehydrate file-type icons in any
    // AngularJS overlay still bound to the browse list. React's own
    // FilePreview does not depend on the event, but custom Angular
    // renderers - and any listener installed by third-party services
    // registered on $rootScope - would otherwise be silently starved
    // once the legacy grid is retired.
    useEffect(() => {
        if (!itemsData || itemsData.length === 0) return;
        $rootScope.$broadcast('reloadBrowseListFileTypes');
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemsData]);

    // LAX-10043: mirror `fetch-item-controller.js#successFn` (line 578).
    // The legacy controller emits `checkUsageLimits` after every
    // successful fetch so navigation-controller.js#193 (via
    // `updateOrganizationUsageLimit`) can refresh the org usage badge.
    // Fire it whenever the ['items'] query settles into a non-fetching
    // state without a fresh error - matches the "successFn on the last
    // in-flight request" semantics of the legacy flow.
    useEffect(() => {
        if (isFetching || isError) return;
        if (!itemsData) return;
        $rootScope.$broadcast('checkUsageLimits');
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFetching, isError, itemsData]);

    // LAX-10043: mirror `fetch-item-controller.js#errorFn` (lines 581-
    // 601). React previously swallowed fetch errors through react-
    // query, hiding the SEARCH_QUERY_TOO_LONG bulk-search fallback and
    // the generic ERROR_LOADING_ITEMS toast. `error` transitions to a
    // fresh object on each failed refetch, so this effect fires once
    // per failure and never for the initial null.
    useEffect(() => {
        if (!isError || !error) return;
        const axiosError = error as {
            response?: { data?: { errorCode?: number } };
        };
        const errorCode = axiosError?.response?.data?.errorCode;
        if (errorCode === ERROR_CODE_SEARCH_QUERY_TOO_LONG) {
            if ($rootScope?.hasSettingFeature?.('BULK_SEARCH')) {
                // Delegate to items-controller.js: it owns $scope.query
                // and $scope.newBulkSearch, so opening the modal from
                // there keeps the keyword-splitting logic in a single
                // place (webapp/app/controllers/items-controller.js
                // lines 1459-1476).
                $rootScope.$broadcast('openBulkSearchOnQueryTooLong');
            } else {
                toast.error(t('ERROR_LOADING_ITEMS'));
            }
            return;
        }
        if (errorCode !== ERROR_CODE_SEARCH_QUERY_INVALID) {
            toast.error(t('ERROR_LOADING_ITEMS'));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isError, error]);

    useAutoFetchIfSpace({
        containerRef,
        itemHeight: 60, // estimated height of one item row
        itemsCount: itemsData?.length || 0,
        itemsData: itemsData,
        pageSize,
        hasNextPage,
        fetchNextPage
    });

    useEffect(() => {
        dirtyItemIdsRef.current = itemsData
            ?.filter(item => item.validation_dirty__ === true)
            .map(item => item.primaryKey__) ?? [];
    }, [itemsData]);

    useItemValidationUpdater(dirtyItemIdsRef);

    // LAX-10041: mirror the legacy `items-controller.js#channelMessageReceived`
    // handler (webapp/app/controllers/items-controller.js lines 126-292) so
    // remote item edits/deletes, validations, publications and mass
    // updates reflect in the visible grid without a full page reload.
    useItemsListRealtimeUpdates({ refetch });

    // LAX-10042: mirror `items-controller.js#hasRestrictions` (lines 394-401).
    // Restrictions are any user-driven filter/search/category the AngularJS
    // side put into `$scope.restrictions` before merging pagination /
    // sortOrder into the broadcast payload. Since the merged `queryParams`
    // always contains pagination noise, filter that out explicitly.
    const hasActiveRestrictions = useMemo(() => {
        if (!queryParams || typeof queryParams !== 'object') return false;
        for (const [key, value] of Object.entries(queryParams)) {
            if (PAGINATION_QUERY_PARAM_KEYS.has(key)) continue;
            if (value === undefined || value === null || value === '') continue;
            return true;
        }
        return false;
    }, [queryParams]);

    // LAX-10042: mirror `items-controller.js#hasAnyCreatePermission` (lines
    // 96-108). The AngularJS handler resolves the current category from
    // `$routeParams` (treating `NO_CATEGORY` and undefined as "no category"
    // via `category = null`) and calls
    // `Auth.hasAnyPermission(OBJECT_TYPE_ITEMS, 'create', {category__: category})`.
    const hasAnyCreatePermission = useMemo(() => {
        if (!auth || isCatalogTable) return false;
        const routeParams = $angularJSInjector.get('$routeParams');
        const rawCategory = routeParams?.category;
        const category =
            !rawCategory || rawCategory === 'NO_CATEGORY' ? null : rawCategory;
        return auth.hasAnyPermission(
            AUTH_OBJECT_TYPES.OBJECT_TYPE_ITEMS,
            'create',
            // Match the AngularJS shape verbatim - Auth.hasAnyPermission
            // accepts a null category and does not fall back to {} when the
            // key is omitted.
            { category__: category } as unknown as Record<string, string>,
        );
        // Re-read $routeParams whenever queryParams changes (i.e. on
        // NewFetchItems, which fires on every category / selectionId
        // change) - same trigger as `storedStateSubKey` above.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auth, isCatalogTable, queryParams]);

    // LAX-10042: the previous branch (`isLoading && !isEmpty(itemsData) &&
    // isEmpty(queryParams)`) was internally contradictory and effectively
    // never fired, which is why users saw nothing before the first fetch or
    // when the very first fetch was still in flight. Show a centered loader
    // whenever we have no items yet AND either the query is disabled (no
    // NewFetchItems broadcast received yet) or an initial fetch is
    // currently in flight. This covers both the "just mounted" state and
    // the "first fetch pending" state.
    const isInitialLoad =
        isEmpty(itemsData)
        && (isEmpty(queryParams) || isLoading || isFetching);
    if (isInitialLoad) {
        return (
            <div
                role="status"
                aria-label="loading"
                className="tw-h-[calc(100vh-200px)] tw-flex tw-items-center tw-justify-center"
            >
                <Loader size="lg" />
            </div>
        );
    }

    if (!isFetching && isEmpty(itemsData) && !isEmpty(queryParams)) {
        // LAX-10042: mirror `browse.tpl.html` lines 36-46 - the empty-state
        // headline flips between NO_ITEMS_HEADLINE ("no items yet") and
        // NO_ITEMS_FOUND ("no items match your search / filter") depending on
        // whether any restriction is active. The NEW_ITEM CTA only appears
        // in the "no items yet" branch and is gated by
        // `hasAnyCreatePermission()` and the availability of the AngularJS
        // `createNewItem` bridge callback. Height matches the grid
        // container so the empty state sits in the same box the grid
        // would otherwise occupy, centered vertically.
        const showCreateCta =
            !isCatalogTable
            && !hasActiveRestrictions
            && !!createNewItem;
        return (
            <div className="tw-h-[calc(100vh-200px)] tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-xl tw-space-y-4">
                <div className="tw-w-16 tw-h-16 tw-bg-gray-300 tw-rounded-full tw-flex tw-items-center tw-justify-center">
                    <Icon
                        icon={hasActiveRestrictions ? NoSymbolIcon : InboxIcon}
                        size="xl"
                        variant="dark"
                        disabled={hasActiveRestrictions}
                    />
                </div>
                <span className="tw-mt-1">
                    {t(
                        hasActiveRestrictions
                            ? 'NODATA.NO_ITEMS_FOUND'
                            : 'NODATA.NO_ITEMS_HEADLINE',
                    )}
                </span>
                {showCreateCta && (
                    <Button
                        variant="default"
                        disabled={!hasAnyCreatePermission}
                        onClick={createNewItem}
                    >
                        {t('NEW_ITEM')}
                    </Button>
                )}
            </div>
        );

    }

    // LAX-10042: during a sort/filter/category refetch, `placeholderData:
    // keepPreviousData` (LAX-9797) intentionally keeps the previous rows on
    // screen so the Grid does not collapse to a single loader row (which
    // would clamp horizontal scrollLeft to 0). That is good for scroll
    // preservation but leaves the user with no visual feedback that a
    // refetch is happening. Pin a small Loader pill to the top of the
    // container (solid white bg + shadow) so the rows stay put and the
    // user unambiguously sees that a fetch is in flight. Next-page
    // fetches have their own in-grid loader row (`isLoadingNextPage`) and
    // must not double up here.
    const isRefetchingList = isFetching && !isFetchingNextPage;

    return (
        <div className='tw-h-[calc(100vh-200px)] tw-relative' ref={containerRef}>
            {isRefetchingList && (
                <div
                    role="status"
                    aria-label="loading"
                    className="tw-absolute tw-inset-0 tw-z-20 tw-flex tw-items-center tw-justify-center tw-pointer-events-none"
                >
                    <Loader size="lg" />
                </div>
            )}
            {
                !hideFoundItemsLabel && (itemsFound && itemsMax && itemsData) && (
                    <>
                        {parseInt(itemsFound) > parseInt(itemsMax) &&
                            <div className="tw-text-right tw-mb-2 tw-mx-3">
                                <span>
                                    {t('ITEM.NUMBER_FOUND_APPROXIMATELY', {
                                        from: 1,
                                        to: itemsData?.length || 1,
                                        count: itemsMax || itemsFound
                                    })}
                                </span>
                            </div>
                        }
                        {(parseInt(itemsFound) >= itemsData?.length && parseInt(itemsFound) <= parseInt(itemsMax) )&&
                            <div className="tw-text-right tw-mb-2 tw-mx-3">
                                <span>
                                    {t('ITEM.NUMBER_FOUND', {
                                        from: 1,
                                        to: itemsData?.length || 1,
                                        count: itemsFound || 1
                                    })}
                                </span>
                            </div>
                        }
                        {(itemWatchList && itemWatchList.primaryKeys && itemWatchList.primaryKeys.length > parseInt(itemsFound) &&
                            <div className="tw-text-right tw-mb-2 tw-mx-3">
                                <span>
                                    {t('ITEM.NUMBER_FOUND', {
                                        from: 1,
                                        to: itemWatchList.primaryKeys?.length || 1,
                                        count: itemWatchList.primaryKeys?.length
                                    })}
                                </span>
                            </div>)}
                    </>
                )
            }
            {(itemWatchList.primaryKeys.length > 0 || allItemsSelected) && (
                <div
                    id="all-items-selected-label"
                    className="tw-p-2 tw-mb-2 tw-text-center tw-bg-secondary-lighter"
                >
                    {!allItemsSelected && <span className="tw-cursor-pointer " onClick={()=> handelLoadWatchListItems()}>
                        {t('ITEM.SHOW_SELECTED', {
                            count: itemWatchList.primaryKeys.length,
                        })}
                    </span>}
                    {allItemsSelected && <span>
                        {t('ITEM.ALL_ITEMS_SELECTED')}
                    </span>}
                    |
                    <span
                        className="tw-cursor-pointer tw-mx-1"
                        onClick={() => handelClearAllSelection()}
                    >
                        {t('ITEM.CLEAR_SELECTION')}
                    </span>
                    |
                    <span
                        className="tw-cursor-pointer tw-ml-1"
                        onClick={() => handelSelectAll()}
                    >
                        {t('ITEM.SELECT_ALL_ITEMS')}
                    </span>
                </div>
            )}

            <Grid
                // LAX-10039: re-mount when the sub-key changes so per-category
                // preferences hydrate from scratch instead of leaking the
                // previous category's in-memory columnSizing/order/sort.
                key={`items-list-grid-${storedStateSubKey ?? 'none'}`}
                thClassName='tw-uppercase'
                storedStateKey = {isCatalogTable ? 'items-list-catalog' : 'items-list-browse'}
                storedStateSubKey={storedStateSubKey}
                columns={ItemsGridColumns}
                isLoading={isLoading}
                burgerMenu={true}
                enableSorting={true}
                manualSorting={true}
                isLoadingNextPage={isFetchingNextPage}
                data={itemsData || []}
                getRowId={(row) => row.primaryKey__}
                onInfiniteScroll={handleInfiniteScroll}
                onSortingChange={handleSortingChange}
                onSelectedRow={isCatalogTable? (row)=>handleSelectedRow(row): undefined}
                enableRowSelection={isCatalogTable}
                isWrapContent={true}
                autoFitColumnOnResizeHandleDoubleClick={true}
                truncateHeaders={true}
                className={
                    'tw-h-full '
                    + '[&_.table-head]:!tw-h-10 [&_.table-head]:!tw-py-0 '
                    + '[&_th[data-column-id=\'select\']]:!tw-p-0 '
                    + '[&_th[data-column-id=\'select\']]:tw-w-10 '
                    + '[&_.grid-row_.scoped-table-cell]:tw-relative '
                    + '[&_.grid-row_.scoped-table-cell]:!tw-py-0 '
                    + '[&_.grid-row_.scoped-table-cell:not([data-column-id=\'actions\']):not(:has([data-hit-list-icon-cell]))>div]:tw-h-full '
                    + '[&_.grid-row_.scoped-table-cell:not([data-column-id=\'actions\']):not(:has([data-hit-list-icon-cell]))>div]:tw-w-full '
                    + '[&_.grid-row_.scoped-table-cell:not([data-column-id=\'actions\']):not(:has([data-hit-list-icon-cell]))>div]:tw-flex '
                    + '[&_.grid-row_.scoped-table-cell:not([data-column-id=\'actions\']):not(:has([data-hit-list-icon-cell]))>div]:tw-items-stretch '
                    + '[&_.grid-row_.scoped-table-cell:not([data-column-id=\'actions\']):not(:has([data-hit-list-icon-cell]))>div]:tw-min-w-0 '
                    + '[&_.grid-row_.scoped-table-cell:not(:has([data-editing-cell]))]:!tw-h-10 '
                    + '[&_.grid-row_.scoped-table-cell:not(.pinned):not(.left-pinned):not(:has([data-editing-cell]))]:tw-overflow-hidden '
                    + '[&_.grid-row_.scoped-table-cell:has([data-editing-cell])]:tw-overflow-visible '
                    + '[&_.grid-row_.scoped-table-cell:has([data-editing-cell])>div]:!tw-overflow-visible'
                }
                rowWrapper={rowWrapper}
            />
        </div>
    );
}

/**
 * Outer wrapper: for the browse layout we mount the `EditingRowProvider`
 * and wire the save/validate callbacks that patch React Query cache +
 * push validations into the shared datamodel Zustand stores. The catalog
 * layout stays fully read-only and skips the provider entirely.
 */
function ItemsList(props: ItemsListComponentProps) {
    const queryClient = useQueryClient();
    const { toast } = useToast();
    const { t } = useTranslation();

    const onSaveSuccess = useCallback(
        ({ savedItem, patch }: SaveSuccessArgs) => {
            // Legacy: after ItemResource.save, the grid row's entity was
            // overwritten with the server response and a growl was shown.
            // We do the equivalent on the React Query cache so any UI
            // reading from ['items'] picks up the fresh row.
            //
            // Merge order matters: `patch` before `savedItem`.
            // `FromItemConverter.setProperty` (mdm) drops null-valued
            // attributes when it serializes the response, so a save that
            // cleared `simple_single_boolean` (unchecked a SingleBoolean,
            // cleared a Combobox, ...) comes back without that key. Without
            // the patch overlay the stale "true" / previous value would
            // linger in the grid until a page refresh - the user's own
            // edit apparently reverting itself right after the save toast.
            // Applying the patch first materializes those cleared keys as
            // `null` in the row, and the server response then wins for
            // anything the backend actually returned (calculations,
            // system fields, dependent attributes).
            interface ItemsPage {
                data: PersistedItem[];
                [key: string]: unknown;
            }
            interface ItemsInfiniteData {
                pages: ItemsPage[];
                [key: string]: unknown;
            }
            const allQueries = queryClient.getQueriesData<ItemsInfiniteData>({
                queryKey: ['items'],
            });
            allQueries.forEach(([key, oldData]) => {
                if (!oldData) return;
                const newPages = oldData.pages.map((page) => ({
                    ...page,
                    data: page.data.map((it) =>
                        it.primaryKey__ === savedItem.primaryKey__
                            ? { ...it, ...patch, ...savedItem }
                            : it,
                    ),
                }));
                queryClient.setQueryData(key, { ...oldData, pages: newPages });
            });
        },
        [queryClient],
    );

    const onSaveError = useCallback(
        (error: unknown, primaryKey: string) => {
            logger.error('ItemsList save failed', primaryKey, error);
            toast.error(t('ERROR_TECHNICAL'));
        },
        [toast, t],
    );

    const onValidateSuccess = useCallback(
        ({ response, attributeNames }: ValidateSuccessArgs) => {
            const {
                validations = {},
                attributeStates,
            } = (response as {
                validations?: Record<string, BaseValidation[]>;
                attributeStates?: Record<string, string[]>;
            }) ?? {};

            // Push per-attribute readonly / hidden into the shared store so
            // every editable cell in the row picks them up. Mirrors legacy
            // isGridAttributeReadOnly / isGridAttributeHidden reactions.
            if (attributeStates) {
                const store = useAttributesStateStore.getState();
                Object.entries(attributeStates).forEach(([name, states]) => {
                    store.clearState(name);
                    if (states.includes('hidden')) store.setHidden(name);
                    if (states.includes('readonly')) store.setReadonly(name);
                });
            }

            // Clear then re-populate the validations store keyed by
            // attribute name (flat items list => fieldPath === attribute
            // name). Datamodel components read this store internally so
            // cells will automatically flip to error / warning styling.
            const store = useValidationsStore.getState();
            const attrsToReset = new Set<string>([
                ...(attributeNames ?? []),
                ...Object.keys(validations),
            ]);
            attrsToReset.forEach((attr) => {
                store.removeErrorByAttributeName(attr);
                store.removeWarningByAttributeName(attr);
            });
            Object.entries(validations).forEach(
                ([attributeName, list]) => {
                    list.forEach((v) => {
                        const validation: Validation = {
                            ...v,
                            attributeName,
                            message: v.message,
                            name: v.name,
                        };
                        if (v.level === 'Warning') {
                            store.addWarning(attributeName, validation);
                        } else {
                            store.addError(attributeName, validation);
                        }
                    });
                },
            );
        },
        [],
    );

    if (props.isCatalogTable) {
        return <ItemsListInner {...props} />;
    }

    return (
        <BrowseItemsListStates>
            <EditingRowProvider
                onSaveSuccess={onSaveSuccess}
                onSaveError={onSaveError}
                onValidateSuccess={onValidateSuccess}
            >
                <ItemsListInner {...props} />
            </EditingRowProvider>
        </BrowseItemsListStates>
    );
}

/**
 * LAX-10042: parity wrapper for the top-level page states rendered by
 * `browse.tpl.html` lines 1-46 (loading / deployment / no-data-model / no-
 * browse-layout). The AngularJS shell currently gates the React mount
 * point (`#items-list`) on the same conditions, so today these branches
 * are defensive - they only fire once the AngularJS shell is stripped
 * (parent LAX-10028). Loading (state 1) is already handled at the
 * DataModelProvider level (`useDataModel.tsx#49-55`) and does not need a
 * second branch here.
 */
const BrowseItemsListStates = ({ children }: PropsWithChildren) => {
    const { organization } = useOrganization();
    const { dataModel } = useDataModel();

    // Mirror `application-controller.js#blockDuringDataModelDeployment`:
    // block the UI only when a deployment is running for an organization
    // that has never had a data model deployed before.
    const isBlockingDeployment =
        !!organization?.isDataModelDeploying && !organization?.dataModelHash;
    if (isBlockingDeployment) {
        return <DeploymentInProgress />;
    }

    // Mirror `browse.tpl.html` line 7: the "no data model" empty state
    // covers both "no data model uploaded" (no hash) and "data model
    // uploaded but has no browse layout". `DataModelDependentPage` already
    // handles the first case; render the same empty state directly for
    // the second.
    if (organization?.dataModelHash && dataModel && !dataModel.hasLayout('browse')) {
        return <NoDataModelEmptyState />;
    }

    return <DataModelDependentPage>{children}</DataModelDependentPage>;
};

export { ItemsList, type ItemsListComponentProps };
export type { ItemsListMeta } from './itemsList.types';
